import {Flashcard} from '../models/resources'

// A student on a train must never wait on the network between cards. Grades are
// applied optimistically and queued; anything that fails to reach the server stays
// in the queue (persisted to localStorage by vuex-persist) and is replayed on the
// next session. `answered_at` travels with each entry so a replayed answer is
// scheduled from when it was really given, not from when the network came back.
const MAX_QUEUE = 200;

const actions = {

  fetchDueFlashcards(context) {
    return new Promise((resolve, reject) => {
      Flashcard.toRehearse().then(
        (res) => {
          context.commit('flashcards', res.body.flashcards || [])
          context.commit('flashcardStats', {
            due_now:   res.body.due_now,
            daily_cap: res.body.daily_cap,
          })
          resolve(res.body)
        },
        (res) => reject(res.body)
      )
    })
  },

  // "Review ahead" for a student who is caught up but wants to keep going.
  fetchFlashcardsAhead(context) {
    return new Promise((resolve, reject) => {
      Flashcard.get({ahead: true}).then(
        (res) => {
          context.commit('flashcards', res.body.flashcards || [])
          resolve(res.body)
        },
        (res) => reject(res.body)
      )
    })
  },

  fetchFlashcardStats(context) {
    return new Promise((resolve, reject) => {
      Flashcard.stats().then(
        (res) => {
          context.commit('flashcardStats', res.body)
          resolve(res.body)
        },
        (res) => reject(res.body)
      )
    })
  },

  addLessonToReviews(context, {lesson_uuid, mode}) {
    return new Promise((resolve, reject) => {
      Flashcard.save({lesson_uuid, mode: mode || 'unknown_to_front'}).then(
        (res) => {
          context.commit('flashcardStats', {due_now: res.body.due_now})
          resolve(res.body)
        },
        (res) => reject(res.body)
      )
    })
  },

  addSentencesToReviews(context, {sentence_ids, mode}) {
    return new Promise((resolve, reject) => {
      Flashcard.save({sentence_ids, mode: mode || 'unknown_to_front'}).then(
        (res) => {
          context.commit('flashcardStats', {due_now: res.body.due_now})
          resolve(res.body)
        },
        (res) => reject(res.body)
      )
    })
  },

  /**
   * Record one answer. Resolves as soon as the grade is queued so the UI can move
   * on; the network round trip happens behind it.
   */
  gradeFlashcard(context, {flashcard, grade}) {
    const entry = {
      id:          flashcard.id,
      grade,
      answered_at: new Date().toISOString(),
    }
    context.commit('queueFlashcardReview', entry)
    context.dispatch('flushFlashcardReviews')
    return Promise.resolve(entry)
  },

  /**
   * Drain the queue oldest-first. Stops at the first network failure so ordering is
   * preserved — two answers to the same card must land in the order they were given
   * or the schedule is computed from the wrong state.
   */
  flushFlashcardReviews(context) {
    if (context.state.flashcardFlushing) {
      return Promise.resolve()
    }
    context.commit('flashcardFlushing', true)

    const step = () => {
      const entry = context.state.flashcardQueue[0]
      if (!entry) {
        context.commit('flashcardFlushing', false)
        return Promise.resolve()
      }

      return Flashcard.review({id: entry.id}, {
        grade:       entry.grade,
        answered_at: entry.answered_at,
      }).then(
        (res) => {
          context.commit('dequeueFlashcardReview', entry)
          if (res.body && res.body.due_now !== undefined) {
            context.commit('flashcardStats', {due_now: res.body.due_now})
          }
          return step()
        },
        (res) => {
          // 4xx means this entry will never succeed (card deleted, not ours,
          // bad grade) — drop it and keep going rather than wedging the queue.
          const status = res && res.status
          if (status >= 400 && status < 500) {
            context.commit('dequeueFlashcardReview', entry)
            return step()
          }
          context.commit('flashcardFlushing', false)
          return Promise.resolve()
        }
      )
    }

    return step()
  },
}

export default actions
export {MAX_QUEUE}
